import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { properties } from '../data/mock';
import { 
  X, Plus, MapPin, Bed, Bath, Square, 
  ArrowLeft, Check, Minus 
} from 'lucide-react';

const PropertyComparison = () => {
  const [searchParams] = useSearchParams();
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [availableProperties, setAvailableProperties] = useState([]);

  useEffect(() => {
    // Get properties from URL params
    const propertyIds = searchParams.get('properties')?.split(',').map(id => parseInt(id)) || [];
    const initialProperties = properties.filter(p => propertyIds.includes(p.id));
    setSelectedProperties(initialProperties);
    
    // Set available properties (excluding already selected ones)
    setAvailableProperties(properties.filter(p => !propertyIds.includes(p.id)));
  }, [searchParams]);

  const addProperty = (propertyId) => {
    if (selectedProperties.length >= 3) {
      alert('You can compare up to 3 properties at once');
      return;
    }
    
    const property = availableProperties.find(p => p.id === parseInt(propertyId));
    if (property) {
      setSelectedProperties([...selectedProperties, property]);
      setAvailableProperties(availableProperties.filter(p => p.id !== property.id));
    }
  };

  const removeProperty = (propertyId) => {
    const property = selectedProperties.find(p => p.id === propertyId);
    if (property) {
      setSelectedProperties(selectedProperties.filter(p => p.id !== propertyId));
      setAvailableProperties([...availableProperties, property]);
    }
  };

  const comparisonFeatures = [
    { key: 'price', label: 'Price', format: (value) => `£${value.toLocaleString()}` },
    { key: 'location', label: 'Location', format: (value) => value },
    { key: 'type', label: 'Property Type', format: (value) => value },
    { key: 'bedrooms', label: 'Bedrooms', format: (value) => value },
    { key: 'bathrooms', label: 'Bathrooms', format: (value) => value },
    { key: 'sqft', label: 'Square Feet', format: (value) => `${value.toLocaleString()} sq ft` },
    { key: 'status', label: 'Status', format: (value) => value }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/properties" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Properties
            </Link>
          </Button>
          
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: '#2E2E2E' }}>
                Property Comparison
              </h1>
              <p className="text-gray-600 mt-2">
                Compare up to 3 properties side by side
              </p>
            </div>
            
            {availableProperties.length > 0 && selectedProperties.length < 3 && (
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-700">Add Property:</span>
                <select
                  onChange={(e) => addProperty(e.target.value)}
                  className="w-64 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2725B] focus:border-transparent"
                  defaultValue=""
                >
                  <option value="" disabled>Select a property to compare</option>
                  {availableProperties.map((property) => (
                    <option key={property.id} value={property.id.toString()}>
                      {property.title}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {selectedProperties.length === 0 ? (
          <div className="text-center py-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Properties Selected</h2>
            <p className="text-gray-600 mb-8">Select properties from our listings to start comparing</p>
            <Button asChild>
              <Link to="/properties">Browse Properties</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Property Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {selectedProperties.map((property) => (
                <Card key={property.id} className="relative">
                  <button
                    onClick={() => removeProperty(property.id)}
                    className="absolute top-4 right-4 z-10 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  
                  <div className="relative">
                    <img 
                      src={property.image} 
                      alt={property.title}
                      className="w-full h-48 object-cover"
                    />
                    <Badge className="absolute top-4 left-4 bg-[#E2725B] text-white">
                      {property.status}
                    </Badge>
                  </div>
                  
                  <CardContent className="p-6">
                    <div className="flex items-center text-gray-600 mb-2">
                      <MapPin className="h-4 w-4 mr-1" />
                      <span className="text-sm">{property.location}</span>
                    </div>
                    <h3 className="text-xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
                      {property.title}
                    </h3>
                    <div className="flex items-center gap-4 text-gray-600 mb-4">
                      <div className="flex items-center">
                        <Bed className="h-4 w-4 mr-1" />
                        <span className="text-sm">{property.bedrooms}</span>
                      </div>
                      <div className="flex items-center">
                        <Bath className="h-4 w-4 mr-1" />
                        <span className="text-sm">{property.bathrooms}</span>
                      </div>
                      <div className="flex items-center">
                        <Square className="h-4 w-4 mr-1" />
                        <span className="text-sm">{property.sqft} sq ft</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-2xl font-bold" style={{ color: '#E2725B' }}>
                        £{property.price.toLocaleString()}
                      </span>
                      <Button size="sm" asChild>
                        <Link to={`/property/${property.id}`}>View Details</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {/* Add Property Card */}
              {selectedProperties.length < 3 && (
                <Card className="border-2 border-dashed border-gray-300 hover:border-[#E2725B] transition-colors">
                  <CardContent className="flex items-center justify-center h-full min-h-[400px]">
                    <div className="text-center">
                      <Plus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Add Another Property</h3>
                      <p className="text-gray-500 mb-4">Compare up to 3 properties</p>
                      {availableProperties.length > 0 ? (
                        <select
                          onChange={(e) => addProperty(e.target.value)}
                          className="w-48 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2725B] focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="" disabled>Select property</option>
                          {availableProperties.map((property) => (
                            <option key={property.id} value={property.id.toString()}>
                              {property.title}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Button variant="outline" asChild>
                          <Link to="/properties">Browse Properties</Link>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Comparison Table */}
            {selectedProperties.length > 1 && (
              <Card>
                <CardHeader>
                  <CardTitle style={{ color: '#2E2E2E' }}>
                    Side-by-Side Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Feature</th>
                          {selectedProperties.map((property) => (
                            <th key={property.id} className="text-left py-3 px-4 font-semibold text-gray-700">
                              {property.title}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonFeatures.map((feature) => (
                          <tr key={feature.key} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium" style={{ color: '#2E2E2E' }}>
                              {feature.label}
                            </td>
                            {selectedProperties.map((property) => (
                              <td key={property.id} className="py-3 px-4 text-gray-700">
                                {feature.format(property[feature.key])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Features Comparison */}
            {selectedProperties.length > 1 && (
              <Card>
                <CardHeader>
                  <CardTitle style={{ color: '#2E2E2E' }}>
                    Features Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Feature</th>
                          {selectedProperties.map((property) => (
                            <th key={property.id} className="text-center py-3 px-4 font-semibold text-gray-700">
                              {property.title}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Get all unique features */}
                        {Array.from(new Set(selectedProperties.flatMap(p => p.features))).map((feature) => (
                          <tr key={feature} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium" style={{ color: '#2E2E2E' }}>
                              {feature}
                            </td>
                            {selectedProperties.map((property) => (
                              <td key={property.id} className="py-3 px-4 text-center">
                                {property.features.includes(feature) ? (
                                  <Check className="h-5 w-5 text-green-500 mx-auto" />
                                ) : (
                                  <Minus className="h-5 w-5 text-gray-300 mx-auto" />
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <div className="flex justify-center gap-4">
              <Button 
                className="bg-gradient-to-r from-[#E2725B] to-[#FF914D] hover:from-[#d65a43] hover:to-[#e8823d] text-white px-8 py-3"
                asChild
              >
                <Link to="/contact">Contact Agent</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/properties">Browse More Properties</Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyComparison;